import React, { useState, useEffect } from 'react';
import { Calendar, Users, FileText, CreditCard, Mail, Check, X, Clock, RefreshCw, Eye, ChevronDown, ChevronUp, Phone, MapPin, Euro, Trash2, Settings, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import AdminPinGuard from '../components/AdminPinGuard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  en: {
    pageTitle: 'Admin Dashboard',
    pageSubtitle: 'Manage your bookings, consultation forms, and payments',
    tabs: {
      bookings: 'Bookings',
      consultations: 'Consultation Forms',
      payments: 'Payments'
    },
    refresh: 'Refresh',
    loading: 'Loading...',
    noData: {
      bookings: 'No bookings yet',
      consultations: 'No consultation forms yet',
      payments: 'No payments recorded yet'
    },
    table: {
      date: 'Date',
      time: 'Time',
      client: 'Client',
      service: 'Service',
      price: 'Price',
      status: 'Status',
      actions: 'Actions',
      treatment: 'Treatment',
      email: 'Email',
      phone: 'Phone',
      amount: 'Amount',
      method: 'Method'
    },
    status: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      completed: 'Completed',
      paid: 'Paid'
    },
    actions: {
      confirm: 'Confirm',
      cancel: 'Cancel',
      complete: 'Mark Complete',
      view: 'View Details',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this item?',
      deleted: 'Successfully deleted'
    },
    modal: {
      consultationDetails: 'Consultation Details',
      personalInfo: 'Personal Information',
      name: 'Name',
      nationality: 'Nationality',
      dob: 'Date of Birth',
      medicalConditions: 'Medical Conditions',
      allergies: 'Allergies',
      lifestyle: 'Lifestyle',
      consent: 'Consent',
      signature: 'Signature',
      close: 'Close'
    },
    stats: {
      totalBookings: 'Total Bookings',
      pendingBookings: 'Pending',
      confirmedBookings: 'Confirmed',
      totalRevenue: 'Total Revenue'
    }
  },
  fr: {
    pageTitle: 'Espace Administration',
    pageSubtitle: 'Gérez vos réservations, fiches de consultation et paiements',
    tabs: {
      bookings: 'Réservations',
      consultations: 'Fiches de Consultation',
      payments: 'Paiements'
    },
    refresh: 'Actualiser',
    loading: 'Chargement...',
    noData: {
      bookings: 'Aucune réservation',
      consultations: 'Aucune fiche de consultation',
      payments: 'Aucun paiement enregistré'
    },
    table: {
      date: 'Date',
      time: 'Heure',
      client: 'Client',
      service: 'Service',
      price: 'Prix',
      status: 'Statut',
      actions: 'Actions',
      treatment: 'Traitement',
      email: 'Email',
      phone: 'Téléphone',
      amount: 'Montant',
      method: 'Méthode'
    },
    status: {
      pending: 'En attente',
      confirmed: 'Confirmé',
      cancelled: 'Annulé',
      completed: 'Terminé',
      paid: 'Payé'
    },
    actions: {
      confirm: 'Confirmer',
      cancel: 'Annuler',
      complete: 'Marquer terminé',
      view: 'Voir détails',
      delete: 'Supprimer',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer cet élément ?',
      deleted: 'Supprimé avec succès'
    },
    modal: {
      consultationDetails: 'Détails de la Consultation',
      personalInfo: 'Informations Personnelles',
      name: 'Nom',
      nationality: 'Nationalité',
      dob: 'Date de naissance',
      medicalConditions: 'Conditions médicales',
      allergies: 'Allergies',
      lifestyle: 'Mode de vie',
      consent: 'Consentement',
      signature: 'Signature',
      close: 'Fermer'
    },
    stats: {
      totalBookings: 'Total Réservations',
      pendingBookings: 'En attente',
      confirmedBookings: 'Confirmées',
      totalRevenue: 'Revenu Total'
    }
  },
  pt: {
    pageTitle: 'Painel de Administração',
    pageSubtitle: 'Gerencie suas reservas, fichas de consulta e pagamentos',
    tabs: {
      bookings: 'Reservas',
      consultations: 'Fichas de Consulta',
      payments: 'Pagamentos'
    },
    refresh: 'Atualizar',
    loading: 'Carregando...',
    noData: {
      bookings: 'Nenhuma reserva',
      consultations: 'Nenhuma ficha de consulta',
      payments: 'Nenhum pagamento registrado'
    },
    table: {
      date: 'Data',
      time: 'Hora',
      client: 'Cliente',
      service: 'Serviço',
      price: 'Preço',
      status: 'Status',
      actions: 'Ações',
      treatment: 'Tratamento',
      email: 'Email',
      phone: 'Telefone',
      amount: 'Valor',
      method: 'Método'
    },
    status: {
      pending: 'Pendente',
      confirmed: 'Confirmado',
      cancelled: 'Cancelado',
      completed: 'Concluído',
      paid: 'Pago'
    },
    actions: {
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      complete: 'Marcar concluído',
      view: 'Ver detalhes'
    },
    modal: {
      consultationDetails: 'Detalhes da Consulta',
      personalInfo: 'Informações Pessoais',
      name: 'Nome',
      nationality: 'Nacionalidade',
      dob: 'Data de Nascimento',
      medicalConditions: 'Condições Médicas',
      allergies: 'Alergias',
      lifestyle: 'Estilo de Vida',
      consent: 'Consentimento',
      signature: 'Assinatura',
      close: 'Fechar'
    },
    stats: {
      totalBookings: 'Total Reservas',
      pendingBookings: 'Pendentes',
      confirmedBookings: 'Confirmadas',
      totalRevenue: 'Receita Total'
    }
  },
  de: {
    pageTitle: 'Admin-Dashboard',
    pageSubtitle: 'Verwalten Sie Ihre Buchungen, Beratungsformulare und Zahlungen',
    tabs: {
      bookings: 'Buchungen',
      consultations: 'Beratungsformulare',
      payments: 'Zahlungen'
    },
    refresh: 'Aktualisieren',
    loading: 'Laden...',
    noData: {
      bookings: 'Keine Buchungen',
      consultations: 'Keine Beratungsformulare',
      payments: 'Keine Zahlungen erfasst'
    },
    table: {
      date: 'Datum',
      time: 'Zeit',
      client: 'Kunde',
      service: 'Service',
      price: 'Preis',
      status: 'Status',
      actions: 'Aktionen',
      treatment: 'Behandlung',
      email: 'E-Mail',
      phone: 'Telefon',
      amount: 'Betrag',
      method: 'Methode'
    },
    status: {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      cancelled: 'Storniert',
      completed: 'Abgeschlossen',
      paid: 'Bezahlt'
    },
    actions: {
      confirm: 'Bestätigen',
      cancel: 'Stornieren',
      complete: 'Als abgeschlossen markieren',
      view: 'Details anzeigen'
    },
    modal: {
      consultationDetails: 'Beratungsdetails',
      personalInfo: 'Persönliche Informationen',
      name: 'Name',
      nationality: 'Nationalität',
      dob: 'Geburtsdatum',
      medicalConditions: 'Erkrankungen',
      allergies: 'Allergien',
      lifestyle: 'Lebensstil',
      consent: 'Einwilligung',
      signature: 'Unterschrift',
      close: 'Schließen'
    },
    stats: {
      totalBookings: 'Buchungen Gesamt',
      pendingBookings: 'Ausstehend',
      confirmedBookings: 'Bestätigt',
      totalRevenue: 'Gesamteinnahmen'
    }
  },
  es: {
    pageTitle: 'Panel de Administración',
    pageSubtitle: 'Gestione sus reservas, fichas de consulta y pagos',
    tabs: {
      bookings: 'Reservas',
      consultations: 'Fichas de Consulta',
      payments: 'Pagos'
    },
    refresh: 'Actualizar',
    loading: 'Cargando...',
    noData: {
      bookings: 'Sin reservas',
      consultations: 'Sin fichas de consulta',
      payments: 'Sin pagos registrados'
    },
    table: {
      date: 'Fecha',
      time: 'Hora',
      client: 'Cliente',
      service: 'Servicio',
      price: 'Precio',
      status: 'Estado',
      actions: 'Acciones',
      treatment: 'Tratamiento',
      email: 'Correo',
      phone: 'Teléfono',
      amount: 'Monto',
      method: 'Método'
    },
    status: {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      cancelled: 'Cancelado',
      completed: 'Completado',
      paid: 'Pagado'
    },
    actions: {
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      complete: 'Marcar completado',
      view: 'Ver detalles',
      delete: 'Eliminar',
      confirmDelete: '¿Está seguro de que desea eliminar este elemento?',
      deleted: 'Eliminado con éxito'
    },
    modal: {
      consultationDetails: 'Detalles de Consulta',
      personalInfo: 'Información Personal',
      name: 'Nombre',
      nationality: 'Nacionalidad',
      dob: 'Fecha de Nacimiento',
      medicalConditions: 'Condiciones Médicas',
      allergies: 'Alergias',
      lifestyle: 'Estilo de Vida',
      consent: 'Consentimiento',
      signature: 'Firma',
      close: 'Cerrar'
    },
    stats: {
      totalBookings: 'Total Reservas',
      pendingBookings: 'Pendientes',
      confirmedBookings: 'Confirmadas',
      totalRevenue: 'Ingresos Totales'
    }
  }
};

const AdminPageContent = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tabs = [
    { id: 'bookings', label: t.tabs.bookings, icon: Calendar },
    { id: 'consultations', label: t.tabs.consultations, icon: FileText },
    { id: 'payments', label: t.tabs.payments, icon: CreditCard },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'bookings') {
        const response = await axios.get(`${API}/admin/bookings`);
        setBookings(response.data);
      } else if (activeTab === 'consultations') {
        const response = await axios.get(`${API}/admin/consultations`);
        setConsultations(response.data);
      } else if (activeTab === 'payments') {
        // Payments are derived from completed bookings for now
        const response = await axios.get(`${API}/admin/bookings`);
        const completedBookings = response.data.filter(b => b.status === 'completed' || b.status === 'confirmed');
        setPayments(completedBookings);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`);
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const deleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting booking:', err);
    }
  };

  const deleteConsultation = async (consultationId) => {
    try {
      await axios.delete(`${API}/admin/consultations/${consultationId}`);
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting consultation:', err);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200',
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };
    const statusLabel = t.status[status] || status;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${colors[status] || 'bg-gray-100 border-gray-200'}`}>
        {statusLabel}
      </span>
    );
  };

  const toggleRowExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Calculate stats
  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed' || b.status === 'completed').length,
    revenue: bookings
      .filter(b => b.status === 'confirmed' || b.status === 'completed')
      .reduce((sum, b) => {
        const price = parseFloat(b.service_price?.replace(/[^\d.]/g, '') || 0);
        return sum + price;
      }, 0)
  };

  return (
    <div className="min-h-screen bg-kamit-beige pt-24 pb-16" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl text-kamit-brown mb-2">
              {t.pageTitle}
            </h1>
            <p className="text-kamit-brown/70">
              {t.pageSubtitle}
            </p>
          </div>
          <Link to="/admin/cms">
            <Button className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full" data-testid="cms-link">
              <Sparkles className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Gestion du Contenu' 
               : language === 'pt' ? 'Gestão de Conteúdo'
               : language === 'de' ? 'Content-Verwaltung'
               : language === 'es' ? 'Gestión de Contenido'
               : 'Content Management'}
            </Button>
          </Link>
          <Link to="/admin/course-content">
            <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full ml-2" data-testid="course-content-link">
              <FileText className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Contenu des Formations' 
               : language === 'pt' ? 'Conteúdo dos Cursos'
               : language === 'de' ? 'Kursinhalte'
               : language === 'es' ? 'Contenido de Cursos'
               : 'Course Content'}
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-kamit-brown/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-kamit-gold/10 rounded-lg">
                <Calendar className="h-5 w-5 text-kamit-gold" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kamit-brown">{stats.total}</p>
                <p className="text-xs text-kamit-brown/60">{t.stats.totalBookings}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-kamit-brown/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kamit-brown">{stats.pending}</p>
                <p className="text-xs text-kamit-brown/60">{t.stats.pendingBookings}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-kamit-brown/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kamit-brown">{stats.confirmed}</p>
                <p className="text-xs text-kamit-brown/60">{t.stats.confirmedBookings}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-kamit-brown/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Euro className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-kamit-brown">{stats.revenue.toFixed(0)}€</p>
                <p className="text-xs text-kamit-brown/60">{t.stats.totalRevenue}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                activeTab === tab.id 
                  ? 'bg-kamit-gold text-white shadow-md' 
                  : 'bg-white text-kamit-brown hover:bg-kamit-gold/10 border border-kamit-brown/10'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-kamit-brown hover:bg-kamit-gold/10 border border-kamit-brown/10 ml-auto"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t.refresh}
          </button>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-kamit-brown/10">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-kamit-gold mx-auto mb-4" />
              <p className="text-kamit-brown/70">{t.loading}</p>
            </div>
          ) : activeTab === 'bookings' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-kamit-brown text-kamit-beige">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.date}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.time}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.client}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.service}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.price}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.status}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamit-brown/10">
                  {bookings.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-kamit-brown/60">
                        {t.noData.bookings}
                      </td>
                    </tr>
                  ) : (
                    bookings.map(booking => (
                      <React.Fragment key={booking.id}>
                        <tr className="hover:bg-kamit-beige/30 cursor-pointer" onClick={() => toggleRowExpand(booking.id)}>
                          <td className="px-4 py-3 text-sm text-kamit-brown">{booking.booking_date}</td>
                          <td className="px-4 py-3 text-sm text-kamit-brown">{booking.booking_time}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-kamit-brown font-medium">{booking.customer_name}</div>
                            <div className="text-xs text-kamit-brown/60">{booking.customer_email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-kamit-brown">{booking.service_name}</td>
                          <td className="px-4 py-3 text-sm text-kamit-gold font-medium">{booking.service_price}</td>
                          <td className="px-4 py-3">{getStatusBadge(booking.status)}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              {booking.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                    className="p-1.5 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                                    title={t.actions.confirm}
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                    className="p-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                    title={t.actions.cancel}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              {booking.status === 'confirmed' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'completed')}
                                  className="p-1.5 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                                  title={t.actions.complete}
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => toggleRowExpand(booking.id)}
                                className="p-1.5 rounded-lg bg-kamit-gold/10 text-kamit-gold hover:bg-kamit-gold/20 transition-colors"
                              >
                                {expandedRows[booking.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => setDeleteConfirm({ type: 'booking', id: booking.id, name: booking.customer_name })}
                                className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                                title={t.actions.delete}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedRows[booking.id] && (
                          <tr className="bg-kamit-beige/20">
                            <td colSpan="7" className="px-4 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                  <Phone className="h-4 w-4 text-kamit-brown/50" />
                                  <span className="text-kamit-brown">{booking.customer_phone}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-kamit-brown/50" />
                                  <span className="text-kamit-brown">{booking.customer_email}</span>
                                </div>
                                {booking.notes && (
                                  <div className="flex items-start gap-2 md:col-span-3">
                                    <FileText className="h-4 w-4 text-kamit-brown/50 mt-0.5" />
                                    <span className="text-kamit-brown/70">{booking.notes}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : activeTab === 'consultations' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-kamit-brown text-kamit-beige">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.date}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.client}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.email}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.treatment}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamit-brown/10">
                  {consultations.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-kamit-brown/60">
                        {t.noData.consultations}
                      </td>
                    </tr>
                  ) : (
                    consultations.map(consultation => (
                      <tr key={consultation.id} className="hover:bg-kamit-beige/30">
                        <td className="px-4 py-3 text-sm text-kamit-brown">
                          {new Date(consultation.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-kamit-brown font-medium">
                          {consultation.first_name} {consultation.last_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-kamit-brown/70">{consultation.contact_email}</td>
                        <td className="px-4 py-3 text-sm text-kamit-brown">{consultation.treatment_name || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <button
                              onClick={() => setSelectedItem(consultation)}
                              className="p-1.5 rounded-lg bg-kamit-gold/10 text-kamit-gold hover:bg-kamit-gold/20 transition-colors"
                              title={t.actions.view}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ type: 'consultation', id: consultation.id, name: `${consultation.first_name} ${consultation.last_name}` })}
                              className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                              title={t.actions.delete}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            // Payments Tab
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-kamit-brown text-kamit-beige">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.date}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.client}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.service}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.amount}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t.table.status}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamit-brown/10">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-kamit-brown/60">
                        {t.noData.payments}
                      </td>
                    </tr>
                  ) : (
                    payments.map(payment => (
                      <tr key={payment.id} className="hover:bg-kamit-beige/30">
                        <td className="px-4 py-3 text-sm text-kamit-brown">{payment.booking_date}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-kamit-brown font-medium">{payment.customer_name}</div>
                          <div className="text-xs text-kamit-brown/60">{payment.customer_email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-kamit-brown">{payment.service_name}</td>
                        <td className="px-4 py-3 text-sm text-kamit-gold font-bold">{payment.service_price}</td>
                        <td className="px-4 py-3">
                          {getStatusBadge(payment.status === 'completed' ? 'paid' : payment.status)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail Modal for Consultations */}
        {selectedItem && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
            onClick={() => setSelectedItem(null)}
          >
            <div 
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" 
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-kamit-brown p-6 rounded-t-2xl z-10">
                <button 
                  onClick={() => setSelectedItem(null)} 
                  className="absolute top-4 right-4 text-kamit-beige/80 hover:text-kamit-beige"
                >
                  <X className="h-6 w-6" />
                </button>
                <h3 className="font-heading text-xl text-kamit-beige">
                  {t.modal.consultationDetails}
                </h3>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Personal Info */}
                <div className="bg-kamit-beige/30 rounded-xl p-4">
                  <h4 className="font-medium text-kamit-brown mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {t.modal.personalInfo}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-kamit-brown">{t.modal.name}:</span>
                      <span className="ml-2 text-kamit-brown/70">{selectedItem.first_name} {selectedItem.last_name}</span>
                    </div>
                    <div>
                      <span className="font-medium text-kamit-brown">Email:</span>
                      <span className="ml-2 text-kamit-brown/70">{selectedItem.contact_email}</span>
                    </div>
                    <div>
                      <span className="font-medium text-kamit-brown">{t.modal.nationality}:</span>
                      <span className="ml-2 text-kamit-brown/70">{selectedItem.nationality}</span>
                    </div>
                    <div>
                      <span className="font-medium text-kamit-brown">{t.modal.dob}:</span>
                      <span className="ml-2 text-kamit-brown/70">{selectedItem.date_of_birth}</span>
                    </div>
                    <div>
                      <span className="font-medium text-kamit-brown">{t.table.treatment}:</span>
                      <span className="ml-2 text-kamit-brown/70">{selectedItem.treatment_name}</span>
                    </div>
                    <div>
                      <span className="font-medium text-kamit-brown">Stress Level:</span>
                      <span className="ml-2 text-kamit-brown/70">{selectedItem.stress_level}/5</span>
                    </div>
                  </div>
                </div>

                {/* Medical Conditions */}
                <div className="bg-red-50/50 rounded-xl p-4">
                  <h4 className="font-medium text-kamit-brown mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-red-500" />
                    {t.modal.medicalConditions}
                  </h4>
                  <p className="text-sm text-kamit-brown/70">
                    {selectedItem.conditions?.length > 0 ? selectedItem.conditions.join(', ') : 'None reported'}
                  </p>
                </div>

                {/* Allergies */}
                <div className="bg-yellow-50/50 rounded-xl p-4">
                  <h4 className="font-medium text-kamit-brown mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-yellow-500" />
                    {t.modal.allergies}
                  </h4>
                  <p className="text-sm text-kamit-brown/70">
                    {selectedItem.allergies?.length > 0 ? selectedItem.allergies.join(', ') : 'None reported'}
                  </p>
                </div>

                {/* Lifestyle */}
                <div className="bg-green-50/50 rounded-xl p-4">
                  <h4 className="font-medium text-kamit-brown mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-green-500" />
                    {t.modal.lifestyle}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    {selectedItem.healthy_eating && <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Healthy Eating ✓</span>}
                    {selectedItem.drink_water && <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Drinks Water ✓</span>}
                    {selectedItem.exercise_regularly && <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Exercises ✓</span>}
                    {selectedItem.sleep_well && <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Sleeps Well ✓</span>}
                    {selectedItem.smoke && <span className="bg-red-100 text-red-700 px-2 py-1 rounded">Smokes</span>}
                    {selectedItem.consume_alcohol && <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Alcohol</span>}
                  </div>
                </div>

                {/* Pregnancy */}
                {(selectedItem.is_pregnant || selectedItem.is_breastfeeding) && (
                  <div className="bg-pink-50/50 rounded-xl p-4">
                    <h4 className="font-medium text-kamit-brown mb-3">Pregnancy Status</h4>
                    <p className="text-sm text-kamit-brown/70">
                      {selectedItem.is_pregnant && `Pregnant - ${selectedItem.pregnancy_months} months`}
                      {selectedItem.is_breastfeeding && 'Currently breastfeeding'}
                    </p>
                  </div>
                )}

                {/* Consent */}
                <div className="bg-kamit-gold/10 rounded-xl p-4">
                  <h4 className="font-medium text-kamit-brown mb-3 flex items-center gap-2">
                    <Check className="h-4 w-4 text-kamit-gold" />
                    {t.modal.consent}
                  </h4>
                  <p className="text-sm text-kamit-brown/70">
                    {selectedItem.consent_accepted ? '✓ Consent accepted' : '✗ Consent not given'}
                  </p>
                  <p className="text-sm text-kamit-brown/70 mt-2">
                    Signed on: {selectedItem.signed_date}
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-kamit-beige/50 p-4 rounded-b-2xl">
                <Button
                  onClick={() => setSelectedItem(null)}
                  className="w-full bg-kamit-brown hover:bg-kamit-dark text-kamit-beige rounded-full"
                >
                  {t.modal.close}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <div 
              className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="font-heading text-xl text-kamit-brown mb-2">
                  {t.actions.delete}
                </h3>
                <p className="text-kamit-brown/70 mb-2">
                  {t.actions.confirmDelete}
                </p>
                <p className="text-kamit-brown font-medium mb-6">
                  "{deleteConfirm.name}"
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setDeleteConfirm(null)}
                    variant="outline"
                    className="flex-1 border-kamit-brown/20 text-kamit-brown hover:bg-kamit-beige rounded-full"
                  >
                    {t.actions.cancel}
                  </Button>
                  <Button
                    onClick={() => {
                      if (deleteConfirm.type === 'booking') {
                        deleteBooking(deleteConfirm.id);
                      } else {
                        deleteConsultation(deleteConfirm.id);
                      }
                    }}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t.actions.delete}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPage = () => {
  return (
    <AdminPinGuard>
      <AdminPageContent />
    </AdminPinGuard>
  );
};

export default AdminPage;
